import React, { useMemo } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import { Container } from 'components/Container'
import { Option, OptionType } from 'components/Option'

import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { useNextStep } from 'hooks/useNextStep'
import { TPageProps } from 'models/common.model'
import { selectUUID } from 'root-redux/selects/user'
import { useSelector } from 'react-redux'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'
import { useStartSession } from 'hooks/useStartSession'
import { AnswerButtonLight } from 'components/AnswerButtonLight'
import { StyledFindYourMoodBalance as S } from './FindYourMoodBalance.styles'
import { OPTION_VALUES, QUESTION } from './constants'

export const FindYourMoodBalance: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
}) => {
  const { t } = useTranslation()
  const uuid = useSelector(selectUUID)
  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()

  const handleContinue = useNextStep({
    pageId,
    question: QUESTION,
    nextPagePath,
  })

  const optionProps = useMemo(
    () => ({
      name: pageId,
      type: OptionType.RADIO,
      onChange: (value: string) => {
        handleChange(value, handleContinue)
        setIsAnswersDisabled(true)

        window.fbq('track', 'CustomizeProduct', {}, { eventID: uuid })
        googleAnalyticsLogger.logFirstPageCompleted()
      },
    }),
    [pageId, handleChange, setIsAnswersDisabled, handleContinue, uuid],
  )

  useStartSession()

  return (
    <Container>
      <S.Root>
        <S.Title>{t('onboarding.findYourMoodBalance.title')}</S.Title>
        <S.Subtitle>
          <Trans
            i18nKey="onboarding.findYourMoodBalance.subtitle"
            components={[<br />]}
          />
        </S.Subtitle>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.IMPROVE_SLEEP_QUALITY}
        >
          <AnswerButtonLight>
            {t('onboarding.findYourMoodBalance.options.improveSleepQuality')}
          </AnswerButtonLight>
        </Option>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.REDUCE_STRESS_OR_ANXIETY}
        >
          <AnswerButtonLight>
            {t('onboarding.findYourMoodBalance.options.reduceStress')}
          </AnswerButtonLight>
        </Option>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.PLAY_RELAXING_GAMES}
        >
          <AnswerButtonLight>
            {t('onboarding.findYourMoodBalance.options.playGames')}
          </AnswerButtonLight>
        </Option>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.FIND_OUT_MORE_ABOUT_MY_MOOD}
        >
          <AnswerButtonLight>
            {t('onboarding.findYourMoodBalance.options.findMore')}
          </AnswerButtonLight>
        </Option>

        <Option
          {...optionProps}
          disabled={isAnswersDisabled}
          value={OPTION_VALUES.SOMETHING_ELSE}
        >
          <AnswerButtonLight>
            {t('onboarding.findYourMoodBalance.options.somethingElse')}
          </AnswerButtonLight>
        </Option>
      </S.Root>
    </Container>
  )
}
